/**
 * Changing the product / pack size of an existing subscription
 * (components/SubscriptionProductChange.jsx, used from the customer's and the
 * admin's Edit subscription dialogs).
 *
 * The store must have it switched on by a super admin; everything here is
 * inert otherwise. The server decides what a change does and what it costs
 * (backend/subscription_product_change.py): the dialog asks for a PREVIEW,
 * shows it, and confirms by sending back the price and first date it showed —
 * if either moved meanwhile the server refuses and returns a fresh preview.
 * So nothing in this file prices anything; it only lists what can be picked
 * and words what the server answered.
 */

/** Exactly true — a store that never had the switch has it off. */
export const isProductChangeOn = (tenant) => tenant?.subscription_product_change_enabled === true;

/** ₹50, ₹42.50 — no rounding of a real paise amount. */
export const money = (n) => {
  const v = Number(n || 0);
  return `₹${v.toLocaleString("en-IN", Number.isInteger(v) ? { maximumFractionDigits: 0 } : { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const shortDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short", timeZone: "UTC" });
};
const listDates = (days) => (days || []).map(shortDate).join(", ");
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/**
 * What can be picked: [{value, productId, variantId, label, price, disabled, why}].
 * A product with one size is one entry; a product with several is one entry
 * per size. The size is always sent under the id the server gave for it —
 * including an id that looks like "<product id>-default", which is a real
 * stored id on some products (the server knows which; nothing is guessed
 * here). A size the store saved WITHOUT an id cannot be addressed, so it is
 * listed but cannot be picked, even when it is the product's only size — a
 * size is never chosen for the customer.
 */
export function productChoices(products) {
  const out = [];
  for (const p of products || []) {
    if (!p || p.is_active === false || p.is_subscribable === false) continue;
    const sizes = (p.variants || []).filter((v) => v.is_active !== false);
    if (sizes.length <= 1) {
      const v = sizes[0];
      const why = v && !v.id ? "not available yet" : v && v.is_in_stock === false ? "out of stock" : "";
      out.push({
        value: `${p.id}|${v?.id || ""}`, productId: p.id, variantId: v?.id || null, label: p.name,
        price: Number(v?.price ?? p.price ?? 0), disabled: !!why, why,
      });
      continue;
    }
    sizes.forEach((v, i) => {
      const why = !v.id ? "not available yet" : v.is_in_stock === false ? "out of stock" : "";
      out.push({
        value: v.id ? `${p.id}|${v.id}` : `${p.id}|#${i}`, productId: p.id, variantId: v.id || null,
        label: `${p.name} · ${v.label || v.pack_size || "Option"}`,
        price: Number(v.price ?? 0), disabled: !!why, why,
      });
    });
  }
  return out;
}

/** The entry the subscription is on now, or "" when it is not in the list. */
export function currentChoiceValue(sub, choices) {
  if (!sub) return "";
  const mine = (choices || []).filter((c) => c.productId === sub.product_id);
  if (mine.length === 1) return mine[0].value;
  return mine.find((c) => c.variantId && c.variantId === sub.variant_id)?.value || "";
}

/** Body of the preview request for a picked entry, or null if it cannot be picked. */
export function previewBody(choices, value) {
  const c = (choices || []).find((x) => x.value === value);
  if (!c || c.disabled) return null;
  return { product_id: c.productId, variant_id: c.variantId };
}

/** Whether the previewed change may be confirmed, given the admin's tick. */
export function canConfirm(preview, { admin = false, acknowledged = false } = {}) {
  if (!preview || preview.no_change) return false;
  if (preview.wallet && preview.wallet.ok === false) return false;
  if (admin && (preview.days_past_cutoff || []).length > 0 && !acknowledged) return false;
  return true;
}

/** Body of the confirm request: the choice plus exactly what the preview showed. */
export function confirmBody(body, preview, { admin = false, acknowledged = false } = {}) {
  const out = {
    ...body, confirm: true,
    expected_unit_price: preview.new.unit_price,
    expected_effective_from: preview.effective_from || null,
  };
  if (admin) out.acknowledge_past_cutoff = !!acknowledged;
  return out;
}

/** The size as the picker names it: the size's own label, else its pack size. */
export const sizeName = (x) => {
  const label = x?.variant_label && x.variant_label !== "Default" ? x.variant_label : "";
  return label || x?.pack_size || "";
};

/** "Farm Fresh Milk (1 Litre)" — the size is left out when the name already says it. */
export const productLabel = (x) => {
  const size = sizeName(x);
  return `${x.product_name}${size && !String(x.product_name).includes(size) ? ` (${size})` : ""}`;
};

/**
 * The preview in words. {from, to, price, discount, perDelivery, when, locked,
 * pastCutoff, wallet, nothing} — each a string, "" when it does not apply.
 */
export function previewLines(preview, { admin = false } = {}) {
  if (!preview) return null;
  const { current, new: next } = preview;
  const whose = admin ? "the customer's" : "your";
  const d = next.discount;
  const n = preview.deliveries_changing || 0;
  return {
    nothing: preview.no_change ? "This subscription is already on this product." : "",
    from: `${productLabel(current)} · ${money(current.unit_price)} each`,
    to: `${productLabel(next)} · ${money(next.unit_price)} each`,
    price: Number(current.unit_price) === Number(next.unit_price)
      ? `The price stays ${money(next.unit_price)} each.`
      : `The price changes from ${money(current.unit_price)} to ${money(next.unit_price)} each.`,
    discount: d
      ? `Includes ${whose} ${d.type === "percent" ? `${d.value}%` : money(d.value)} discount on ${d.applies_to} (list price ${money(next.catalogue_price)}).`
      : "",
    perDelivery: `First delivery it applies to: ${money(preview.per_delivery.current)} → ${money(preview.per_delivery.new)}.`,
    when: preview.effective_from
      ? `Applies from ${shortDate(preview.effective_from)} — ${plural(n, "upcoming delivery", "upcoming deliveries")} will change.`
      : "No upcoming delivery can be changed right now; it applies from the next one that is still open.",
    locked: (preview.days_locked || []).length ? `Stays as ordered (already locked): ${listDates(preview.days_locked)}.` : "",
    pastCutoff: admin && (preview.days_past_cutoff || []).length
      ? `Past the ordering cutoff and may already be prepared: ${listDates(preview.days_past_cutoff)}. These will change too.`
      : "",
    wallet: preview.wallet && preview.wallet.ok === false ? (preview.wallet.message || "The wallet balance cannot cover this change.") : "",
  };
}

/**
 * What a confirm amounted to:
 *   "done"    — changed, and every upcoming delivery it reaches was brought over;
 *   "pending" — changed, but some upcoming deliveries are still being updated
 *               (the server retries; see the pending marker on the backend);
 *   "nothing" — the subscription was already on that product.
 * Only "done" may be reported as a success, and only "done" closes the dialog.
 */
export function changeOutcome(result) {
  if (!result?.changed) return "nothing";
  return result.sync_complete === false ? "pending" : "done";
}

/** Toast after a confirm that is "done" (or "nothing"). */
export function changedMessage(result) {
  if (!result?.changed) return "This subscription is already on this product.";
  const n = result.deliveries_updated || 0;
  return `Product changed · ${plural(n, "upcoming delivery", "upcoming deliveries")} updated`;
}

/** Said, and kept on screen, when the change is saved but not finished. */
export function pendingMessage(result) {
  const n = result?.days_pending;
  const left = Number.isInteger(n) && n > 0 ? plural(n, "upcoming delivery is", "upcoming deliveries are") : "some upcoming deliveries are";
  return `The product was changed, but ${left} still being updated. This is retried automatically — please check the upcoming deliveries again in a few minutes.`;
}

/**
 * Whether the Edit dialog's own form differs from how it was opened. A
 * product change closes the dialog, so it is not offered while this is true —
 * the customer's other edits are never thrown away behind their back. Both
 * arguments are flat objects with the same fields; a list of days is compared
 * regardless of order.
 */
export function hasUnsavedEdits(opened, now) {
  if (!opened || !now) return false;
  const flat = (f) => JSON.stringify(Object.keys(f).sort().map((k) => [k, Array.isArray(f[k]) ? [...f[k]].sort() : f[k]]));
  return flat(opened) !== flat(now);
}

/**
 * What a failed request means for the dialog: {message, preview, conflict}.
 * A refusal that carries a fresh preview (the price or the first date moved,
 * or an admin has not yet acknowledged past-cutoff days) is shown again, not
 * treated as a failure. `conflict` (HTTP 409) WITHOUT a fresh preview means
 * what is on screen no longer describes the subscription — someone else
 * changed it, or an earlier change is still being applied — so the preview
 * must be thrown away and asked for again, never confirmed as it stands.
 */
export function refusal(err, fallback) {
  const detail = err?.response?.data?.detail;
  const conflict = err?.response?.status === 409;
  if (detail && typeof detail === "object") {
    return { message: detail.message || fallback, preview: detail.preview || null, conflict };
  }
  return { message: typeof detail === "string" && detail.trim() ? detail : fallback, preview: null, conflict };
}

/**
 * What an Edit dialog should show after a product change was answered.
 * `open` is the subscription the dialog is showing NOW (null when closed),
 * `subscriptionId` the one the change was made for.
 *   - the dialog shows another subscription, or is closed → left exactly as
 *     it is (the answer may arrive after the customer has moved on);
 *   - done → closed (null);
 *   - otherwise → the same dialog with the new product on it. The id is never
 *     taken from the answer, so a dialog can never be re-pointed at another
 *     subscription.
 */
export function dialogAfterChange(open, subscriptionId, result, outcome) {
  if (!open || open.id !== subscriptionId) return open;
  if (outcome === "done") return null;
  const { id: _never, ...fields } = result?.subscription || {};
  return { ...open, ...fields };
}
